import { Routes, Route, NavLink, useNavigate, useLocation } from "react-router-dom";
import { AdminProvider, useAdmin } from "@/admin/AdminContext";
import NewClientDialog from "@/admin/NewClientDialog";
import NewServiceDialog from "@/admin/NewServiceDialog";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import Dashboard from "@/admin/Dashboard";
import Clients from "@/admin/Clients";
import ClientDetail from "@/admin/ClientDetail";
import Services from "@/admin/Services";
import Payments from "@/admin/Payments";
import ServiceRequests from "@/admin/ServiceRequests";
import Support from "@/admin/Support";
import Team from "@/admin/Team";
import Settings from "@/admin/Settings";
import TestData from "@/admin/TestData";
import {
  LayoutDashboard, Users, Package, CreditCard, LifeBuoy, UserCog, Settings as SettingsIcon,
  Plus, LogOut, MoreHorizontal, Inbox,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/admin/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/admin/requests", label: "Service Requests", icon: Inbox, testid: "nav-requests" },
  { to: "/admin/services", label: "Services", icon: Package, testid: "nav-services" },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, testid: "nav-payments" },
  { to: "/admin/support", label: "Support", icon: LifeBuoy, testid: "nav-support" },
  { to: "/admin/team", label: "Team", icon: UserCog, testid: "nav-team", superOnly: true },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const MOBILE_NAV = NAV.filter((n) => ["Dashboard", "Clients", "Services", "Payments", "Settings"].includes(n.label));

function Shell() {
  const { user, logout } = useAuth();
  const { openNewClient, openNewService } = useAdmin();
  const navigate = useNavigate();
  const isSuper = user.role === "super_admin";
  const nav = NAV.filter((n) => !n.superOnly || isSuper);

  return (
    <div className="min-h-screen bg-[#F8FAF9]">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col fixed inset-y-0 left-0 w-60 bg-[#0D3B2D] text-white z-40">
        <div className="px-5 h-16 flex items-center border-b border-white/10">
          <Logo variant="dark" size="sm" />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-[#67F2B3] text-[#071A13]" : "text-[#F4FFF9]/75 hover:bg-white/10 hover:text-white"
                }`
              }>
              <n.icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-2 px-2 py-2 text-xs text-[#F4FFF9]/70 truncate">
            {user.picture && <img src={user.picture} alt="" className="h-6 w-6 rounded-full" />}
            <span className="truncate">{user.email}</span>
          </div>
          <button onClick={logout} data-testid="logout-button"
            className="flex items-center gap-2 px-2 py-2 text-sm text-[#F4FFF9]/75 hover:text-white w-full">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="md:pl-60">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E2EBF0]">
          <div className="px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
            <div className="md:hidden"><Logo size="sm" /></div>
            <div className="hidden md:block" />
            <div className="flex items-center gap-2">
              <Button onClick={openNewClient} data-testid="quick-add-client-button" variant="outline"
                className="border-[#E2EBF0] text-[#0D3B2D] hover:bg-[#F4FFF9] rounded-lg text-sm">
                <Plus className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">New </span>Client
              </Button>
              <Button onClick={() => openNewService()} data-testid="quick-add-service-button"
                className="bg-[#67F2B3] hover:bg-[#4DE6A3] text-[#071A13] font-semibold rounded-lg text-sm">
                <Plus className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">New </span>Service
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="md:hidden h-9 w-9 flex items-center justify-center rounded-lg border border-[#E2EBF0] text-[#0D3B2D]" data-testid="mobile-more-menu">
                    <MoreHorizontal className="h-5 w-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate("/admin/support")} data-testid="m-nav-support">
                    <LifeBuoy className="h-4 w-4 mr-2" /> Support
                  </DropdownMenuItem>
                  {isSuper && (
                    <DropdownMenuItem onClick={() => navigate("/admin/team")} data-testid="m-nav-team">
                      <UserCog className="h-4 w-4 mr-2" /> Team
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate("/admin/settings")} data-testid="m-nav-settings-menu">
                    <SettingsIcon className="h-4 w-4 mr-2" /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} data-testid="m-logout">
                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 pb-28 md:pb-10 max-w-6xl mx-auto">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="clients" element={<Clients />} />
            <Route path="clients/:clientId" element={<ClientDetail />} />
            <Route path="requests" element={<ServiceRequests />} />
            <Route path="services" element={<Services />} />
            <Route path="payments" element={<Payments />} />
            <Route path="support" element={<Support />} />
            {isSuper && <Route path="team" element={<Team />} />}
            {isSuper && <Route path="test-data" element={<TestData />} />}
            <Route path="settings" element={<Settings />} />
          </Routes>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-[#E2EBF0] z-40">
        <div className="grid grid-cols-5">
          {MOBILE_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`m-${n.testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-medium ${
                  isActive ? "text-[#10B981]" : "text-[#4A635A]"
                }`
              }>
              <n.icon className="h-5 w-5" /> {n.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <NewClientDialog onCreated={(client, addService) => { if (addService) openNewService(client.client_id); }} />
      <NewServiceDialog />
    </div>
  );
}

export default function AdminLayout() {
  return (
    <AdminProvider>
      <Shell />
    </AdminProvider>
  );
}
